import { TemplateCategory } from '@heidi/schema/template-gallery';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  type CommunityTemplateSortKey,
  communityTemplateSortField,
  GET_FORM_TEMPLATES,
} from '../../contracts';
import { getPublicTemplates } from '../api/community';

/**
 * The gallery's FORM-category list (D2.7, ARCFE-1046).
 *
 * Sibling of `useQueryCommunityTemplates` over the same endpoint, with two
 * differences that are shipped behaviour and are preserved rather than
 * reconciled:
 *
 *  1. the cache key property is `searchQuery`, where the community list spells
 *     the same value `search`;
 *  2. the category filter is pinned to `FORM_TEMPLATE` unconditionally instead
 *     of being a caller-supplied filter.
 *
 * The category stays an INVARIANT inside this hook rather than becoming a
 * parameter, and the lint rule is what settled it: a parameter is a queryKey
 * dependency, so `@tanstack/query/exhaustive-deps` correctly demanded it be
 * added to the key — which would have changed the cache identity of every form
 * gallery page. It is not caller-varying data, so it is not an input. The enum
 * value is imported from the generated schema, not retyped as a literal.
 */
export interface FormTemplatesQueryInput {
  sort: CommunityTemplateSortKey | undefined;
  location: string | undefined;
  specialty: string | undefined;
  specialtyGroups: string[] | undefined;
  /** Already debounced by the delegate — the 500ms timer is app orchestration. */
  searchQuery: string | undefined;
  page: number;
  enabled: boolean;
}

export function useQueryFormTemplates({
  sort,
  location,
  specialty,
  specialtyGroups,
  searchQuery,
  page,
  enabled,
}: FormTemplatesQueryInput) {
  return useQuery({
    retry: 3,
    queryKey: [
      GET_FORM_TEMPLATES,
      { sort, location, specialty, specialtyGroups, searchQuery, page },
    ],
    enabled,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchOnMount: true,

    queryFn: async () => {
      const sortParam =
        sort &&
        `${communityTemplateSortField[sort]}:${sort === 'ALPHABETICAL' ? '1' : '-1'}`;
      const locationParam = location ? `country:eq:${location}` : undefined;
      const specialtyParam = specialty ? `specialty:eq:${specialty}` : undefined;
      // Always filter to FORM_TEMPLATE category
      const categoryParam = `template_category:eq:${TemplateCategory.FORM_TEMPLATE}`;

      const { data } = await getPublicTemplates({
        filter: [locationParam, specialtyParam, categoryParam],
        sort: sortParam,
        search: searchQuery,
        page,
        specialtyGroups,
      });
      return data;
    },
    placeholderData: keepPreviousData,
  });
}
